import { FileTypeMeta, excelXlsxType } from "@/Utils/fileValidation";
import { getFileExtension } from "@/Utils/getFileExtension";
import { useTrans } from "@/translations";

import { ChangeEvent, useCallback, useMemo, useRef } from "react";
import { useDropzone } from "react-dropzone";

import { twMerge } from "tailwind-merge";

export const LibraryUploadZone = ({
  allowedFileTypes = [excelXlsxType],
  onAddFile,
  uploadZoneStyle = "",
  topTextKey,
  bottomTextKey,
  isLoading,
}: {
  allowedFileTypes?: FileTypeMeta[];
  onAddFile: (file: File, clear: () => void) => void;
  uploadZoneStyle?: string;
  topTextKey: string;
  bottomTextKey: string;
  isLoading?: boolean;
}) => {
  const { t } = useTrans();
  const inputRef = useRef<HTMLInputElement>(null);

  const triggerFileUpload = () => {
    if (!isLoading) {
      inputRef.current?.click();
    }
  };

  const clear = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }, [inputRef]);

  const allowedExtensions = useMemo(
    () =>
      allowedFileTypes
        .flatMap((type) => type.extensions)
        .map((ext) => `.${ext}`),
    [allowedFileTypes],
  );

  const { getRootProps } = useDropzone({
    noClick: true,
    maxFiles: 1,
    onDrop: (files) => {
      const file = files[0];
      const fileExtension = getFileExtension(file.name);
      if (allowedExtensions.includes(`.${fileExtension}`)) {
        onAddFile(file, clear);
      }
    },
  });

  const onFileAddedWithButton = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files?.length) {
        const file = e.target.files[0];
        const fileExtension = getFileExtension(file.name);

        if (allowedExtensions.includes(`.${fileExtension}`)) {
          onAddFile(file, clear);
        }
      }
    },
    [onAddFile, clear, allowedExtensions],
  );

  return (
    <div
      className={twMerge(
        "bg-grey1 flex h-60 w-full flex-col items-center justify-center border border-dashed border-black p-4",
        !isLoading && "cursor-pointer",
        uploadZoneStyle,
      )}
      {...getRootProps()}
      onClick={triggerFileUpload}
    >
      {isLoading ? (
        <div className="text-sm leading-6 font-normal">
          {t("library:loadingDocumentUpload")}
        </div>
      ) : (
        <>
          <div className="text-sm leading-6 font-medium">{t(topTextKey)}</div>
          <div className="text-sm leading-6 font-normal">
            {t(bottomTextKey)}
          </div>
          <input
            type="file"
            ref={inputRef}
            className="hidden"
            onChange={onFileAddedWithButton}
            accept={allowedExtensions.join(", ")}
            multiple={false}
            data-1p-ignore
          />
        </>
      )}
    </div>
  );
};
